import { useBusiness } from '../context/BusinessContext';
import { 
  hasPermission, 
  hasAnyPermission, 
  hasAllPermissions, 
  canAccessRoute 
} from '../utils/permissions';
import { getPermissionMessage } from '../utils/permissionMessages';
import PermissionDeniedBanner from '../components/Common/PermissionDeniedBanner';

/**
 * Custom hook for checking user permissions
 * Provides easy access to permission checking functions with current user context
 */
export const usePermissions = () => {
  const { selectedBusiness } = useBusiness();
  
  // Get current user's permissions
  const userPermissions = selectedBusiness?.permissions || [];
  
  /**
   * Check if current user has a specific permission
   * @param {string} permissionKey - The permission key to check
   * @returns {boolean}
   */
  const can = (permissionKey) => {
    if (selectedBusiness?.role === 'owner' || selectedBusiness?.role === 'admin') {
      return true;
    }
    return hasPermission(userPermissions, permissionKey);
  };
  
  /**
   * Check if current user has any of the specified permissions
   * @param {Array} permissionKeys - Array of permission keys to check
   * @returns {boolean}
   */
  const canAny = (permissionKeys) => {
    if (selectedBusiness?.role === 'owner' || selectedBusiness?.role === 'admin') {
      return true;
    }
    return hasAnyPermission(userPermissions, permissionKeys);
  };
  
  /**
   * Check if current user has all of the specified permissions
   * @param {Array} permissionKeys - Array of permission keys to check
   * @returns {boolean}
   */
  const canAll = (permissionKeys) => {
    if (selectedBusiness?.role === 'owner' || selectedBusiness?.role === 'admin') {
      return true;
    }
    return hasAllPermissions(userPermissions, permissionKeys);
  };
  
  /**
   * Check if current user can access a specific route
   * @param {string} route - The route path
   * @returns {boolean}
   */
  const canAccess = (route) => {
    return canAccessRoute(userPermissions, route);
  };
  
  /**
   * Get all permissions the current user has
   * @returns {Array} Array of permission keys
   */
  const getPermissions = () => {
    return userPermissions;
  };
  
  /**
   * Check if current user is owner
   * @returns {boolean}
   */
  const isOwner = () => {
    return selectedBusiness?.role === 'owner';
  };
  
  /**
   * Check if current user is manager or owner
   * @returns {boolean}
   */
  const isManager = () => {
    return selectedBusiness?.role === 'manager' || selectedBusiness?.role === 'owner';
  };
  
  /**
   * Check if current user is staff, manager, or owner
   * @returns {boolean}
   */
  const isStaff = () => {
    return ['staff', 'manager', 'owner'].includes(selectedBusiness?.role);
  };
  
  return {
    can,
    canAny,
    canAll,
    canAccess,
    getPermissions,
    isOwner,
    isManager,
    isStaff,
    userPermissions,
    role: selectedBusiness?.role
  };
};

/**
 * Higher-order component for protecting routes based on permissions
 * @param {Component} Component - The component to protect
 * @param {string|Array} requiredPermissions - Required permission(s)
 * @param {Component} fallbackComponent - Component to show if permission denied
 * @returns {Component} Protected component
 */
export const withPermissions = (Component, requiredPermissions, fallbackComponent = null) => {
  return function ProtectedComponent(props) {
    const { can, canAny } = usePermissions();
    
    // Convert single permission to array
    const permissions = Array.isArray(requiredPermissions) 
      ? requiredPermissions 
      : [requiredPermissions];
    
    // Check if user has required permissions
    const hasRequiredPermissions = permissions.length === 1 
      ? can(permissions[0])
      : canAny(permissions);
    
    if (!hasRequiredPermissions) {
    if (fallbackComponent) {
      return fallbackComponent;
    }
    
    return (
      <PermissionDeniedBanner
        {...getPermissionMessage('PAGE_ACCESS')}
      />
    );
    }
    
    return <Component {...props} />;
  };
};

/**
 * Component for conditionally rendering content based on permissions
 * @param {Object} props - Component props
 * @param {string|Array} props.permission - Required permission(s)
 * @param {React.ReactNode} props.children - Content to render if permission granted
 * @param {React.ReactNode} props.fallback - Content to render if permission denied
 * @returns {React.ReactNode}
 */
export const PermissionGate = ({ permission, children, fallback = null }) => {
  const { can, canAny } = usePermissions();
  
  // Convert single permission to array
  const permissions = Array.isArray(permission) ? permission : [permission];
  
  // Check if user has required permissions
  const hasRequiredPermissions = permissions.length === 1 
    ? can(permissions[0])
    : canAny(permissions);
  
  if (!hasRequiredPermissions) {
    return fallback;
  }
  
  return children;
};
